/**
 * Links out to the GitLab pipeline and job that ran this agent.
 *
 * The URLs are built server-side (backend `services/run_links_service.py`,
 * joining `Project` -> `GitServer`) and never constructed here: this
 * frontend has no GitLab base URL of its own, and building one from a
 * numeric project id alone isn't possible anyway (no project path).
 *
 * `pipelineUrl`/`jobUrl` are independently nullable from `pipelineId`/
 * `jobId`: an id can be known (the run has a real GitLab pipeline/job)
 * while its URL is null (the project/server couldn't be resolved for
 * display). In that case this still shows the id as plain text rather
 * than silently dropping it, but never as a link — a guessed/malformed
 * URL would be worse than none.
 */

import type { ReactElement } from 'react';

interface RunReferencesProps {
  pipelineId: number | null;
  pipelineUrl: string | null;
  jobId: number | null;
  jobUrl: string | null;
}

function reference(id: number | null, url: string | null, label: string): ReactElement | null {
  if (id == null) return null;
  const text = `${label} #${String(id)}`;
  return url != null ? (
    <a href={url} target="_blank" rel="noreferrer">
      {text}
    </a>
  ) : (
    <span>{text}</span>
  );
}

export function RunReferences({
  pipelineId,
  pipelineUrl,
  jobId,
  jobUrl,
}: RunReferencesProps): ReactElement | null {
  if (pipelineId == null && jobId == null) return null;

  return (
    <p className="run-detail__references">
      {reference(pipelineId, pipelineUrl, 'GitLab pipeline')}
      {pipelineId != null && jobId != null && ' · '}
      {reference(jobId, jobUrl, 'job')}
      <span className="run-detail__references-hint"> (the job trace lives in GitLab, not Sanctum)</span>
    </p>
  );
}
